/**
 * Renders the parsed YAML frontmatter of a skill as a key/value block. Standard
 * keys (name, description) get hero treatment at the top; remaining keys render
 * as a compact key:value list below.
 */
interface Props {
  name: string;
  description: string;
  frontmatter: Record<string, unknown>;
}

const HERO_KEYS = new Set(['name', 'description']);

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function SkillFrontmatter({ name, description, frontmatter }: Props) {
  const otherEntries = Object.entries(frontmatter).filter(([k]) => !HERO_KEYS.has(k));

  return (
    <div
      style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        {name}
      </div>
      {description && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--text-soft)',
            lineHeight: 1.5,
            marginBottom: otherEntries.length ? 12 : 0,
          }}
        >
          {description}
        </div>
      )}
      {otherEntries.length > 0 && (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 16,
            rowGap: 6,
            margin: 0,
            fontSize: 12,
          }}
        >
          {otherEntries.map(([k, v]) => {
            const rendered = stringifyValue(v);
            const isMultiline = rendered.includes('\n');
            return (
              <Row key={k} k={k} v={rendered} multiline={isMultiline} />
            );
          })}
        </dl>
      )}
    </div>
  );
}

function Row({ k, v, multiline }: { k: string; v: string; multiline: boolean }) {
  return (
    <>
      <dt
        style={{
          color: 'var(--text-mute)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 11,
        }}
      >
        {k}
      </dt>
      <dd
        style={{
          color: 'var(--text)',
          margin: 0,
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          fontFamily: multiline
            ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
            : 'inherit',
          fontSize: multiline ? 11 : 12,
          wordBreak: 'break-word',
        }}
      >
        {v}
      </dd>
    </>
  );
}
